import {
  type CSSProperties,
  type FC,
  type MouseEvent as ReactMouseEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Spin,
} from "antd";
import dayjs from "dayjs";
import {
  useTranslation,
} from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChevronLeft,
  ChevronRight,
  Package,
} from "lucide-react";
import {
  getApiLangFromI18n,
} from "src/utils/apiLang";
import {
  getOptionalTenantId,
  useShopifySalesProductOverview,
} from "../hooks/useShopifySalesProductAnalytics";
import {
  buildChannelBarData,
  type ChannelBarPoint,
} from "../utils/salesChannelDistributionChartModel";
import {
  getDashboardKpiLucideIconComponent,
} from "src/utils/dashboardKpiLucideIcon";
import CustomChartTooltip from "../../../components/reusable/CustomChartTooltip/CustomChartTooltip";
import {
  DashboardNarrativePanel,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableScroll,
  DashboardDataTableTable,
  DashboardSectionHeader,
  DashboardExportButton,
  dashboardDataTable as dt,
} from "src/components/common";
import {
  useDashboardNarrative,
} from "src/hooks/useDashboardNarrative";
import OrganicDateRangePicker from "src/dashboards/organicMedia/components/Tabs/OrganicDateRangePicker";
import {
  CHART_PRIMARY_COLOR,
} from "src/components/charts/chartPalette";
import {
  type TrendGranularity,
  TREND_PAGE_SIZE,
  formatCurrencyTooltipValue,
  TOTAL_SALES_METRIC_LABEL,
  isIsoDate,
  formatTrendAxisDate,
  formatTrendTooltipDate,
  localizeChartDigits,
  useComparisonLabelLocalizer,
  rtlChartProps,
  SectionTitle,
  MetricCard,
  ChannelBarTooltip,
  OTHERS_TOOLTIP_TWO_COLUMN_MIN,
  EMPTY_TOP_PRODUCTS,
  normalizeProductTypeLabel,
  TopProductsTable,
} from "./salesProductShared";
import {
  useLanguage,
} from "src/hooks/useLanguage";

const TREND_CHART_MARGIN = { top: 4, right: 24, bottom: 10, left: 20 };

/** Overview KPI card labels are built from English constants in `salesProductApiNormalize.ts` (fallback shape) or may already arrive localized from the API — map known English labels to a translated display string while keeping `card.label` itself as the lookup key for tooltip/icon maps. */
const OVERVIEW_KPI_LABEL_KEYS: Record<string, string> = {
  "total sales": "salesProduct.overview.kpi.totalSales",
  "refunds": "salesProduct.overview.kpi.refunds",
  "orders": "salesProduct.overview.kpi.orders",
  "avg. order value": "salesProduct.overview.kpi.avgOrderValue",
  "customers ordered": "salesProduct.overview.kpi.customersOrdered",
  "new customers this week": "salesProduct.overview.kpi.newCustomersThisWeek",
  "inventory value": "salesProduct.overview.kpi.inventoryValue",
  "customers in period": "salesProduct.overview.kpi.customersInPeriod",
};

export const OverviewSection: FC<{
  dateRange: { startDate: string; endDate: string };
  setDateRange: (_range: { startDate: string; endDate: string }) => void;
}> = ({ dateRange, setDateRange }) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const chartRtl = rtlChartProps(isRTL);
  const lang = getApiLangFromI18n(i18n);
  const pdfContainerRef = useRef<HTMLDivElement | null>(null);
  const translateOverviewCardLabel = (label: string) => {
    const key = OVERVIEW_KPI_LABEL_KEYS[label.trim().toLowerCase()];
    return key ? t(key) : label;
  };
  const localizeComparisonLabel = useComparisonLabelLocalizer();
  const tenantId = getOptionalTenantId();
  const { data, rawData, loading, error } = useShopifySalesProductOverview(
    dateRange.startDate,
    dateRange.endDate,
    lang,
    tenantId,
  );

  const currency = data?.currencyLabel ?? "PKR";
  const trendData = useMemo(() => data?.trend ?? [], [data?.trend]);
  const selectedRangeTrendData = useMemo(() => {
    const start = dayjs(dateRange.startDate);
    const end = dayjs(dateRange.endDate);
    if (!start.isValid() || !end.isValid()) {
      return trendData;
    }
    return trendData.filter(point => {
      if (!isIsoDate(point.date)) {
        return true;
      }
      const pointDate = dayjs(point.date);
      return (
        pointDate.isValid() &&
        !pointDate.isBefore(start, "day") &&
        !pointDate.isAfter(end, "day")
      );
    });
  }, [dateRange.endDate, dateRange.startDate, trendData]);

  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("daily");
  const [currentTrendPage, setCurrentTrendPage] = useState(1);

  const aggregatedTrendData = useMemo(() => {
    if (trendGranularity === "daily") {return selectedRangeTrendData;}
    const byMonth = new Map<string, number>();
    for (const point of selectedRangeTrendData) {
      const monthKey = isIsoDate(point.date) ? point.date.slice(0, 7) : point.date;
      byMonth.set(monthKey, (byMonth.get(monthKey) ?? 0) + point.revenue);
    }
    return Array.from(byMonth.entries()).map(([date, revenue]) => ({ date, revenue }));
  }, [selectedRangeTrendData, trendGranularity]);

  const trendTotalPages = Math.max(1, Math.ceil(aggregatedTrendData.length / TREND_PAGE_SIZE));
  const paginatedTrendData = useMemo(
    () => aggregatedTrendData.slice(
      (currentTrendPage - 1) * TREND_PAGE_SIZE,
      currentTrendPage * TREND_PAGE_SIZE,
    ),
    [aggregatedTrendData, currentTrendPage],
  );

  useEffect(() => { setCurrentTrendPage(1); }, [dateRange.startDate, dateRange.endDate, trendGranularity]);
  useEffect(() => { setCurrentTrendPage(p => Math.min(p, trendTotalPages)); }, [trendTotalPages]);

  const channelData = useMemo(() => data?.channels ?? [], [data?.channels]);
  const channelOthersLabel = t("salesProduct.overview.channelOthersLabel", { defaultValue: "Others" });
  const channelBarData = useMemo(
    () => buildChannelBarData(channelData, channelOthersLabel),
    [channelData, channelOthersLabel]
  );

  const [currentChannelPage, setCurrentChannelPage] = useState(1);
  const CHANNEL_PAGE_SIZE = 6;
  const channelTotalPages = Math.max(1, Math.ceil(channelBarData.length / CHANNEL_PAGE_SIZE));
  const paginatedChannelBarData = useMemo(
    () => channelBarData.slice(
      (currentChannelPage - 1) * CHANNEL_PAGE_SIZE,
      currentChannelPage * CHANNEL_PAGE_SIZE,
    ),
    [channelBarData, currentChannelPage],
  );

  useEffect(() => { setCurrentChannelPage(p => Math.min(p, channelTotalPages)); }, [channelTotalPages]);

  const channelBarPercentMax = useMemo(
    () => paginatedChannelBarData.reduce((max, row) => Math.max(max, row.percent), 0),
    [paginatedChannelBarData],
  );
  const channelBarAxisMax = useMemo(
    () => Math.max(1, channelBarPercentMax * 1.22),
    [channelBarPercentMax],
  );
  const channelChartWrapRef = useRef<HTMLDivElement | null>(null);
  const [activeChannelTooltip, setActiveChannelTooltip] = useState<ChannelBarPoint | null>(null);
  const [channelTooltipAnchor, setChannelTooltipAnchor] = useState<{
    top: number;
    left: number;
    width: number;
    caretLeft: number;
  } | null>(null);

  const handleChannelBarHover = (
    point: ChannelBarPoint,
    event: ReactMouseEvent<SVGElement>,
  ) => {
    const wrap = channelChartWrapRef.current;
    if (!wrap) {
      return;
    }
    const barRect = event.currentTarget.getBoundingClientRect();
    const wrapRect = wrap.getBoundingClientRect();
    const barCenterY = barRect.top - wrapRect.top + barRect.height / 2;
    // Bars grow leftwards in RTL, so their "end" is the left edge.
    const barEndX = (isRTL ? barRect.left : barRect.right) - wrapRect.left;
    const othersCount = point.othersItems?.length ?? 0;
    const tooltipWidth =
      point.label === "Others"
        ? othersCount >= OTHERS_TOOLTIP_TWO_COLUMN_MIN
          ? 480
          : 232
        : 260;
    let left = barEndX - tooltipWidth / 2;
    const maxLeft = wrapRect.width - tooltipWidth - 8;
    left = Math.max(8, Math.min(left, maxLeft));
    setActiveChannelTooltip(point);
    setChannelTooltipAnchor({
      top: barCenterY,
      left,
      width: tooltipWidth,
      caretLeft: Math.max(24, barEndX - left - 14),
    });
  };

  const handleChannelBarLeave = () => {
    setActiveChannelTooltip(null);
    setChannelTooltipAnchor(null);
  };
  const metricCards = (data?.metricCards ?? []).filter(card => {
    const key = card.label.trim().toLowerCase();
    return (
      key !== "new customers this week" &&
      key !== "inventory value" &&
      key !== "customers in period"
    );
  });
  const overviewMetricIcons = useMemo(
    () => metricCards.map(card => getDashboardKpiLucideIconComponent(card.label)),
    [metricCards],
  );
  const topProductsByOrders = data?.topProductsByOrders ?? data?.topProducts ?? EMPTY_TOP_PRODUCTS;
  const topProductsByRevenue = data?.topProductsByRevenue ?? data?.topProducts ?? EMPTY_TOP_PRODUCTS;
  const sortedTopProductsByOrders = useMemo(
    () => [...topProductsByOrders]
      .sort((left, right) => right.orders - left.orders)
      .slice(0, 10),
    [topProductsByOrders],
  );
  const sortedTopProductsByRevenue = useMemo(
    () => [...topProductsByRevenue]
      .sort((left, right) => right.revenue - left.revenue)
      .slice(0, 10),
    [topProductsByRevenue],
  );
  const overviewKpiRowStyle = useMemo(
    () =>
      metricCards.length > 0
        ? ({
          display: "grid",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          gridTemplateColumns: `repeat(${metricCards.length}, minmax(0, 1fr))`,
        } satisfies CSSProperties)
        : undefined,
    [metricCards.length],
  );
  const narrativePayloadData = useMemo(
    () => rawData && typeof rawData === "object" && !Array.isArray(rawData)
      ? rawData as Record<string, unknown>
      : {},
    [rawData],
  );
  const { narrative, loading: narrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "sales_product_analytics",
    tabKey: "overview",
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    data: narrativePayloadData,
    enabled: !loading && !error && Boolean(rawData),
  });

  return (
    <div
      className="sales-product-analytics-dashboard__section sales-product-analytics-dashboard__section--inventory"
      ref={pdfContainerRef}
    >
      <DashboardSectionHeader
        title={t("salesProduct.overview.header.title")}
        subtitle={t("salesProduct.overview.header.subtitle")}
        sticky
        actions={(
          <>
            <OrganicDateRangePicker
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onRangeChange={setDateRange}
            />
            <DashboardExportButton
              dashboardName="Sales-Overview"
              dateRange={dateRange}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
            />
          </>
        )}
      />

      {error ? (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />
      ) : null}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={narrative}
          loading={loading || narrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      <Spin
        spinning={loading}
        wrapperClassName="sales-product-analytics-dashboard__overview-spin"
      >
        <div
          className="sales-product-analytics-dashboard__metrics sales-product-analytics-dashboard__metrics--overview"
          style={overviewKpiRowStyle}
          data-pdf-block="kpi-cards"
        >
          {metricCards.length
            ? metricCards.map((card, index) => {
              const Icon = overviewMetricIcons[index] ?? getDashboardKpiLucideIconComponent(card.label);
              const lowerIsBetterLabels = new Set(["refunds", "total taxes", "taxes", "expenses", "cogs"]);
              const cardPolarity = lowerIsBetterLabels.has(card.label.trim().toLowerCase())
                ? "lower-is-better" as const
                : "higher-is-better" as const;
              return (
                <MetricCard
                  key={`${card.label}-${index}`}
                  label={card.label}
                  displayLabel={translateOverviewCardLabel(card.label)}
                  value={card.value}
                  changePercent={card.changePercent}
                  changePercentFormattedValue={card.changePercentFormattedValue}
                  comparisonLabel={localizeComparisonLabel(card.comparisonLabel)}
                  statusHint={card.statusHint}
                  tone={card.tone ?? "neutral"}
                  icon={Icon}
                  tooltipAlign={index >= 3 ? "end" : "start"}
                  trendPolarity={cardPolarity}
                />
              );
            })
            : !loading
              ? (
                <p className="sales-product-analytics-dashboard__card-subtitle" style={{ gridColumn: "1 / -1" }}>
                  {t("salesProduct.overview.noKpiData")}
                </p>
              )
              : null}
        </div>

        <div
          className="sales-product-analytics-dashboard__grid sales-product-analytics-dashboard__grid--overview-charts"
          data-pdf-row="charts-row"
        >
          <section
            className="sales-product-analytics-dashboard__card sales-product-analytics-dashboard__card--overview-chart"
            data-pdf-block="trend-chart"
          >
            <div className="sales-product-analytics-dashboard__overview-chart-head">
              <SectionTitle title={t("salesProduct.overview.trendTitle")} tooltipKey="Total Sales Trend" />
              <div
                className="spa-trend-granularity"
                role="group"
                aria-label={t("salesProduct.overview.trendGranularityAria")}
              >
                {(["daily", "monthly"] as const).map(option => (
                  <button
                    key={option}
                    type="button"
                    className={
                      trendGranularity === option
                        ? "spa-trend-granularity__btn spa-trend-granularity__btn--active"
                        : "spa-trend-granularity__btn"
                    }
                    onClick={() => setTrendGranularity(option)}
                  >
                    {option === "daily" ? t("salesProduct.overview.daily") : t("salesProduct.overview.monthly")}
                  </button>
                ))}
              </div>
            </div>
            <div className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--overview">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={paginatedTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7edf5" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    reversed={chartRtl.axisReversed}
                    tickFormatter={(v: string) => localizeChartDigits(
                      trendGranularity === "monthly"
                        ? (dayjs(v).isValid() ? dayjs(v).format("MMM YY") : v)
                        : formatTrendAxisDate(v),
                      isRTL,
                    )}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    orientation={chartRtl.yAxisOrientation}
                    tickFormatter={value => localizeChartDigits(`${currency} ${Math.round(value / 1000)}k`, isRTL)}
                  />
                  <Tooltip
                    content={
                      <CustomChartTooltip
                        formatTooltip={(value: number) =>
                          localizeChartDigits(formatCurrencyTooltipValue(value, currency), isRTL)}
                        formatLabel={(v: string) => localizeChartDigits(
                          trendGranularity === "monthly"
                            ? (dayjs(v).isValid() ? dayjs(v).format("MMMM YYYY") : v)
                            : formatTrendTooltipDate(v),
                          isRTL,
                        )}
                      />
                    }
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="revenue"
                    name={t("salesProduct.overview.kpi.totalSales", { defaultValue: TOTAL_SALES_METRIC_LABEL })}
                    stroke={CHART_PRIMARY_COLOR}
                    strokeWidth={3}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div
              className="spa-trend-pagination"
              role="navigation"
              aria-label={t("salesProduct.overview.pageOf", { current: currentTrendPage, total: trendTotalPages })}
            >
              <span className="spa-trend-pagination__label">
                {t("salesProduct.overview.pageOf", { current: currentTrendPage, total: trendTotalPages })}
              </span>
              <div className="spa-trend-pagination__nav">
                <button
                  type="button"
                  className="spa-trend-pagination__btn"
                  disabled={currentTrendPage <= 1}
                  onClick={() => setCurrentTrendPage(p => Math.max(1, p - 1))}
                  aria-label={t("salesProduct.overview.previousPage")}
                >
                  {isRTL ? <ChevronRight size={16} aria-hidden /> : <ChevronLeft size={16} aria-hidden />}
                </button>
                <button
                  type="button"
                  className="spa-trend-pagination__btn"
                  disabled={currentTrendPage >= trendTotalPages}
                  onClick={() => setCurrentTrendPage(p => Math.min(trendTotalPages, p + 1))}
                  aria-label={t("salesProduct.overview.nextPage")}
                >
                  {isRTL ? <ChevronLeft size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                </button>
              </div>
            </div>
          </section>

          <section
            className="sales-product-analytics-dashboard__card sales-product-analytics-dashboard__card--overview-chart"
            data-pdf-block="channel-bars"
          >
            <div className="sales-product-analytics-dashboard__overview-chart-head">
              <SectionTitle title={t("salesProduct.overview.channelChartTitle")} tooltipKey="Total Sales By Channel" />
            </div>
            {channelBarData.length ? (
              <div className="sales-product-analytics-dashboard__overview-channel-body">
                <div
                  ref={channelChartWrapRef}
                  className="sales-product-analytics-dashboard__chart-wrap sales-product-analytics-dashboard__chart-wrap--overview sales-product-analytics-dashboard__chart-wrap--overview-channel-bars"
                  style={
                    {
                      "--overview-channel-rows": String(paginatedChannelBarData.length),
                    } as CSSProperties
                  }
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={paginatedChannelBarData}
                      layout="vertical"
                      margin={TREND_CHART_MARGIN}
                      barCategoryGap="22%"
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        horizontal={false}
                        stroke="#e7edf5"
                        vertical
                      />
                      <XAxis
                        type="number"
                        domain={[0, channelBarAxisMax]}
                        ticks={[
                          0,
                          channelBarAxisMax / 2,
                          channelBarAxisMax,
                        ]}
                        tick={{ fontSize: 11, fill: "#94a3b8" }}
                        tickLine={false}
                        axisLine={{ stroke: "#e2e8f0" }}
                        tickFormatter={value => localizeChartDigits(
                          Math.round(typeof value === "number" ? value : Number(value)).toString(),
                          isRTL,
                        )}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={90}
                        tick={{ fontSize: 13, fill: "#17325c", fontWeight: 500 }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Bar
                        dataKey="percent"
                        radius={[0, 6, 6, 0]}
                        barSize={38}
                        maxBarSize={38}
                        onMouseLeave={handleChannelBarLeave}
                      >
                        {paginatedChannelBarData.map(item => (
                          <Cell
                            key={item.label}
                            fill={item.color}
                            onMouseEnter={event => handleChannelBarHover(item, event)}
                            onMouseLeave={handleChannelBarLeave}
                          />
                        ))}
                        <LabelList
                          dataKey="displayLabel"
                          position="right"
                          offset={8}
                          formatter={(value: string) => localizeChartDigits(String(value), isRTL)}
                          className="sales-product-analytics-dashboard__channel-bar-label"
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  {activeChannelTooltip && channelTooltipAnchor ? (
                    <div
                      className="sales-product-analytics-dashboard__channel-tooltip-floating"
                      style={{
                        top: channelTooltipAnchor.top + 8,
                        left: channelTooltipAnchor.left,
                        width: channelTooltipAnchor.width,
                        ["--channel-tooltip-caret-left" as string]: `${channelTooltipAnchor.caretLeft}px`,
                      }}
                    >
                      <ChannelBarTooltip point={activeChannelTooltip} currencyLabel={currency} />
                    </div>
                  ) : null}
                </div>
                {channelTotalPages > 1 && (
                  <div className="spa-trend-pagination">
                    <span className="spa-trend-pagination__label">
                      {t("salesProduct.overview.pageOf", { current: currentChannelPage, total: channelTotalPages })}
                    </span>
                    <div className="spa-trend-pagination__nav">
                      <button
                        type="button"
                        className="spa-trend-pagination__btn"
                        disabled={currentChannelPage <= 1}
                        onClick={() => setCurrentChannelPage(p => Math.max(1, p - 1))}
                        aria-label={t("salesProduct.overview.previousPage")}
                      >
                        {isRTL ? <ChevronRight size={16} aria-hidden /> : <ChevronLeft size={16} aria-hidden />}
                      </button>
                      <button
                        type="button"
                        className="spa-trend-pagination__btn"
                        disabled={currentChannelPage >= channelTotalPages}
                        onClick={() => setCurrentChannelPage(p => Math.min(channelTotalPages, p + 1))}
                        aria-label={t("salesProduct.overview.nextPage")}
                      >
                        {isRTL ? <ChevronLeft size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="sales-product-analytics-dashboard__card-subtitle">
                {t("salesProduct.overview.noChannelData")}
              </p>
            )}
          </section>
        </div>

        <div
          className="sales-product-analytics-dashboard__grid sales-product-analytics-dashboard__grid--top-products"
          data-pdf-row="tables-row"
        >
          <DashboardDataTablePanel
            as="section"
            className="sales-product-analytics-dashboard__table-card sales-product-analytics-dashboard__table-card--top-products"
            data-pdf-block="top-products-table"
            data-pdf-splittable
          >
            <DashboardDataTablePanelHeader>
              <SectionTitle
                title={t("salesProduct.overview.topProductsByOrders")}
                tooltipKey="Top 10 Products by Orders"
              />
            </DashboardDataTablePanelHeader>
            <DashboardDataTableScroll className="sales-product-analytics-dashboard__table-wrap">
              <DashboardDataTableTable
                className={`${dt.tableCompact} sales-product-analytics-dashboard__table sales-product-analytics-dashboard__table--overview sales-product-analytics-dashboard__table--top-products`}
              >
                <thead>
                  <tr>
                    <th className="sales-product-analytics-dashboard__product-image-heading">{t("salesProduct.overview.columnImage")}</th>
                    <th>{t("salesProduct.overview.columnProductName")}</th>
                    <th className={dt.number}>{t("salesProduct.overview.columnOrders")}</th>
                    <th>{t("salesProduct.overview.columnCategory")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTopProductsByOrders.length
                    ? sortedTopProductsByOrders.map(product => (
                      <tr key={product.id}>
                        <td className="sales-product-analytics-dashboard__product-image-cell">
                          <span className="sales-product-analytics-dashboard__product-image-frame">
                            {product.imageUrl ? (
                              <img
                                src={product.imageUrl}
                                alt=""
                                loading="lazy"
                                onError={event => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <Package className="sales-product-analytics-dashboard__product-image-placeholder" />
                            )}
                          </span>
                        </td>
                        <td>
                          <span className="sales-product-analytics-dashboard__product-name">
                            {product.name}
                          </span>
                        </td>
                        <td className={dt.number}>{product.ordersFormattedValue ?? product.orders.toLocaleString()}</td>
                        <td>{normalizeProductTypeLabel(product.productType, t("salesProduct.shared.notAvailable"))}</td>
                      </tr>
                    ))
                    : (
                      <tr>
                        <td colSpan={4}>{t("salesProduct.overview.noTopProductsOrders")}</td>
                      </tr>
                    )}
                </tbody>
              </DashboardDataTableTable>
            </DashboardDataTableScroll>
          </DashboardDataTablePanel>

          <TopProductsTable
            title={t("salesProduct.overview.topProductsByRevenue")}
            tooltipKey="Top 10 Products by Gross Sales"
            products={sortedTopProductsByRevenue}
            valueKey="revenue"
            valueLabel={t("salesProduct.overview.grossSales")}
            currency={currency}
            emptyLabel={t("salesProduct.overview.noTopProductsRevenue")}
            dataPdfBlock="top-products-revenue-table"
            dataPdfSplittable
          />
        </div>
      </Spin>
    </div>
  );
};
